import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button, Badge, Input, Select } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  REMINDER_STATUS_LABELS,
  canDeleteReminder,
  canEditReminder,
  defaultDueLocalValue,
  fromDatetimeLocalValue,
  reminderAssigneeName,
  reminderCreatorName,
  reminderLeadName,
  toDatetimeLocalValue,
} from '../../lib/reminderUtils';

const { FiCheck, FiX, FiEdit2, FiTrash2 } = FiIcons;

const EMPTY_FORM = {
  title: '',
  notes: '',
  due_at_local: '',
  assigned_to: '',
  notify_email: true,
};

export function formatReminderDue(iso) {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return '—';
  }
}

export function ReminderStatusBadge({ status }) {
  const variant =
    status === 'completed' ? 'success' : status === 'dismissed' ? 'default' : 'warning';
  return <Badge variant={variant}>{REMINDER_STATUS_LABELS[status] || status}</Badge>;
}

export function ReminderFormModal({
  isOpen,
  onClose,
  onSave,
  saving = false,
  users = [],
  currentUserId,
  initial = null,
  allowLeadPicker = false,
  leads = [],
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        title: initial.title || '',
        notes: initial.notes || '',
        due_at_local: toDatetimeLocalValue(initial.due_at) || defaultDueLocalValue(),
        assigned_to: initial.assigned_to || currentUserId || '',
        notify_email: initial.notify_email !== false,
        lead_id: initial.lead_id || '',
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        due_at_local: defaultDueLocalValue(),
        assigned_to: currentUserId || '',
        lead_id: '',
      });
    }
  }, [isOpen, initial, currentUserId]);

  const userOptions = useMemo(
    () =>
      (users || [])
        .filter((u) => u && !u.archived_at && u.is_active !== false)
        .map((u) => ({
          value: u.id,
          label: u.full_name?.trim() || 'User',
        })),
    [users],
  );

  const leadOptions = useMemo(
    () => [
      { value: '', label: 'No linked lead' },
      ...(leads || []).map((l) => ({
        value: l.id,
        label: [l.first_name, l.last_name].filter(Boolean).join(' ') || l.email || 'Lead',
      })),
    ],
    [leads],
  );

  const submit = (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const dueAt = fromDatetimeLocalValue(form.due_at_local);
    if (!title || !dueAt || !form.assigned_to) return;
    onSave({
      title,
      notes: form.notes.trim() || null,
      due_at: dueAt,
      assigned_to: form.assigned_to,
      notify_email: Boolean(form.notify_email),
      lead_id: allowLeadPicker ? (form.lead_id || null) : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initial ? 'Edit reminder' : 'New reminder'}
      className="max-w-lg"
    >
      <form onSubmit={submit} className="space-y-3 mt-3">
        <Input
          label="Title"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          required
          placeholder="Follow up with…"
        />
        <div>
          <label className="block text-sm font-medium text-navy mb-1">Notes</label>
          <textarea
            className="flex w-full rounded-[10px] border border-lineGray bg-white px-3 py-2 text-sm placeholder:text-slateText focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional context"
          />
        </div>
        <Input
          label="Due"
          type="datetime-local"
          value={form.due_at_local}
          onChange={(e) => setForm((f) => ({ ...f, due_at_local: e.target.value }))}
          required
        />
        <Select
          label="Assignee"
          value={form.assigned_to}
          onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))}
          options={userOptions}
        />
        {allowLeadPicker && (
          <Select
            label="Linked lead (optional)"
            value={form.lead_id || ''}
            onChange={(e) => setForm((f) => ({ ...f, lead_id: e.target.value }))}
            options={leadOptions}
          />
        )}
        <label className="flex items-center gap-2 text-sm text-navy">
          <input
            type="checkbox"
            checked={Boolean(form.notify_email)}
            onChange={(e) => setForm((f) => ({ ...f, notify_email: e.target.checked }))}
          />
          Email me / the assignee when this is due
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saving} disabled={!form.title.trim() || !form.assigned_to}>
            {initial ? 'Save' : 'Create reminder'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function ReminderRow({
  row,
  role,
  viewerId,
  onComplete,
  onDismiss,
  onEdit,
  onDelete,
  busyId,
  showLead = true,
}) {
  const pending = row.status === 'pending';
  const canEdit = canEditReminder(role, row, viewerId);
  const canDelete = canDeleteReminder(role, row, viewerId);
  const leadName = reminderLeadName(row);
  const busy = busyId === row.id;

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-3.5 rounded-[14px] border border-lineGray bg-white">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold text-navy">{row.title}</p>
          <ReminderStatusBadge status={row.status} />
        </div>
        <p className="text-xs text-slateText mt-1">
          Due {formatReminderDue(row.due_at)} · {reminderAssigneeName(row)}
          {row.created_by && row.created_by !== row.assigned_to
            ? ` · from ${reminderCreatorName(row)}`
            : ''}
        </p>
        {showLead && leadName && row.lead_id && (
          <p className="text-xs mt-1">
            <Link to={`/leads/${row.lead_id}?tab=reminders`} className="font-semibold text-primary hover:underline">
              {leadName}
            </Link>
          </p>
        )}
        {row.notes && <p className="text-[12.5px] text-slateText mt-1.5 whitespace-pre-wrap">{row.notes}</p>}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          {pending && (
            <>
              <Button size="sm" onClick={() => onComplete(row)} disabled={busy} isLoading={busy}>
                <SafeIcon icon={FiCheck} className="h-3.5 w-3.5 mr-1" />
                Complete
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDismiss(row)} disabled={busy}>
                <SafeIcon icon={FiX} className="h-3.5 w-3.5 mr-1" />
                Dismiss
              </Button>
            </>
          )}
          {pending && (
            <Button size="sm" variant="ghost" onClick={() => onEdit(row)} disabled={busy}>
              <SafeIcon icon={FiEdit2} className="h-3.5 w-3.5" />
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="ghost" onClick={() => onDelete(row)} disabled={busy}>
              <SafeIcon icon={FiTrash2} className="h-3.5 w-3.5 text-red-500" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
